import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";

interface WhatsAppAudioPlayerProps {
  src: string;
  onError: () => Promise<void> | void;
  tr: (es: string, en: string) => string;
}

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function WhatsAppAudioPlayer({
  src,
  onError,
  tr,
}: WhatsAppAudioPlayerProps) {
  const END_SNAP_EPSILON = 0.5;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Don't carry the previous audio's progress over while the new one loads.
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onTimeUpdate = () => {
      const nextCurrentTime = audio.currentTime || 0;
      const nextDuration = audio.duration || 0;
      if (nextDuration > 0 && nextCurrentTime >= nextDuration - END_SNAP_EPSILON) {
        setCurrentTime(nextDuration);
        return;
      }
      setCurrentTime(nextCurrentTime);
    };
    const onEnded = () => {
      setIsPlaying(false);
      const finalDuration = audio.duration || 0;
      setDuration((previous) => finalDuration || previous);
      setCurrentTime((previous) => finalDuration || previous);
    };
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("play", onPlay);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("play", onPlay);
    };
  }, [src]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      try {
        await audio.play();
      } catch (error) {
        console.error("Failed to play audio:", error);
      }
      return;
    }

    audio.pause();
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextTime = Number(e.target.value);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const progressPercent =
    duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

  return (
    <div className="w-full">
      <audio ref={audioRef} preload="metadata" src={src} onError={onError} />

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={togglePlayPause}
          className="h-6 w-6 -ml-0.5 text-gray-500 hover:text-gray-600 flex items-center justify-center flex-shrink-0 transition-colors"
          title={isPlaying ? tr("Pausar", "Pause") : tr("Reproducir", "Play")}
        >
          {isPlaying ? (
            <span className="flex items-center gap-[2px]">
              <span className="h-3.5 w-[3px] rounded-[1px] bg-current" />
              <span className="h-3.5 w-[3px] rounded-[1px] bg-current" />
            </span>
          ) : (
            <Play className="h-[18px] w-[18px] ml-0.5 fill-current stroke-0" />
          )}
        </button>

        <div className="relative flex-1 min-w-0 h-6">
          <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-gray-300" />
          <div
            className="absolute left-1 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-gray-800"
            style={{ width: `calc((100% - 8px) * ${progressPercent / 100})` }}
          />
          <div
            className="absolute inset-x-1 top-1/2 -translate-y-1/2 h-0 overflow-visible"
            style={{ pointerEvents: "none" }}
          >
            <div
              className="absolute top-1/2 h-[14px] w-[14px] -translate-y-1/2 -translate-x-1/2 rounded-full border-2 border-white bg-gray-500"
              style={{ left: `${progressPercent}%` }}
            />
          </div>

          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(currentTime, duration || 0)}
            onChange={handleSeek}
            className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
            aria-label={tr("Posición del audio", "Audio position")}
          />
        </div>
      </div>

      <div className="text-[9px] leading-none text-green-900 flex justify-between pl-7 pr-0.5 mt-0.5">
        <span>{formatAudioTime(currentTime)}</span>
        <span>{formatAudioTime(duration)}</span>
      </div>
    </div>
  );
}
